// automationRuleUtils.js

import { generateKey } from 'utilities/Keys';
import { truncateText } from 'utilities/Strings';
import { parseLiteral } from './automationValueUtils';
import {
  buildDefaultConditionLiteral,
  getAllowedConditionOperators,
  getDefaultConditionOperator,
  isDateLikeFieldType,
} from './automationConditionFilters';

export { getAllowedConditionOperators } from './automationConditionFilters';
export { getConditionOperatorLabel } from './automationConditionFilters';
export { getDefaultConditionOperator } from './automationConditionFilters';

export const ENUM_MULTI_CONDITION_OPERATORS = new Set(['is_any_of', 'is_none_of']);

export const getAutomationRuleTitle = (rule) => {
  const normalizedTitle = String(rule?.title || '').trim();
  if (normalizedTitle) return normalizedTitle;

  const legacyName = String(rule?.name || '').trim();
  if (legacyName) return legacyName;

  return 'Untitled automation';
};

export const getAutomationRuleDescription = (rule) => {
  const normalizedDescription = String(rule?.description || '').trim();
  if (normalizedDescription) return normalizedDescription;

  const legacyName = String(rule?.name || '').trim();
  if (legacyName) return legacyName;

  return '';
};

const UNTITLED_AUTOMATION_LABEL = 'Untitled automation';

function isUntitledAutomationLabel(value) {
  return String(value || '').trim().toLowerCase() === UNTITLED_AUTOMATION_LABEL;
}

export const getAutomationRuleDisplayName = (rule) => {
  const title = getAutomationRuleTitle(rule);
  if (!isUntitledAutomationLabel(title)) return title;

  const description = getAutomationRuleDescription(rule);
  if (description) return description;

  return 'Untitled Automation';
};

export const getAutomationExplorerLabel = (rule, maxLength = 48) => {
  const fullLabel = getAutomationRuleDisplayName(rule);
  const normalizedMaxLength = Number(maxLength);

  if (!Number.isFinite(normalizedMaxLength) || normalizedMaxLength <= 0 || fullLabel.length <= normalizedMaxLength) {
    return {
      fullLabel,
      label: fullLabel,
    };
  }

  return {
    fullLabel,
    label: truncateText(fullLabel, normalizedMaxLength),
  };
};

const normalizeDatabaseKey = (...values) => {
  for (const value of values) {
    const normalized = String(value || '').trim();
    if (normalized) return normalized;
  }
  return '';
};

const isAllowedEmailBulletFieldKey = (fieldKey, fieldTypeByKey) => {
  const normalizedFieldKey = String(fieldKey || '').trim().toLowerCase();
  if (!normalizedFieldKey) return false;

  const normalizedType = String(fieldTypeByKey?.get(fieldKey) || '').trim().toLowerCase();
  const hasBlockedToken = (value) => (
    value.includes('comment') || value.includes('activity')
  );

  if (normalizedType === 'tags' || hasBlockedToken(normalizedType)) return false;
  if (normalizedFieldKey === 'tags') return false;
  if (hasBlockedToken(normalizedFieldKey)) return false;

  return true;
};

export const normalizeConditionOperator = (operator, fieldType) => {
  const normalizedOperator = String(operator || '').trim().toLowerCase();
  const normalizedFieldType = String(fieldType || '').trim().toLowerCase();

  if (!normalizedOperator) {
    return getDefaultConditionOperator(normalizedFieldType);
  }

  if (normalizedFieldType === 'enum') {
    if (normalizedOperator === 'is') return 'equals';
    if (normalizedOperator === 'not_equals') return 'is_not';
    if (normalizedOperator === 'is_not') return 'is_not';
    if (normalizedOperator === 'equals') return 'equals';
  }

  if (normalizedFieldType === 'tags' || normalizedFieldType === 'people') {
    if (normalizedOperator === 'is') return 'equals';
    if (normalizedOperator === 'is_not') return 'not_contains';
    if (normalizedOperator === 'not_equals') return 'not_contains';
  }

  if (normalizedOperator === 'is') return 'equals';
  if (normalizedOperator === 'is_not') return 'not_equals';

  return normalizedOperator;
};

export const ensureLiteralValue = (raw) => {
  if (raw && typeof raw === 'object' && Object.prototype.hasOwnProperty.call(raw, 'literal')) {
    return raw.literal;
  }
  return raw ?? '';
};

export const buildDefaultAction = ({ databaseKey, typeKey }) => {
  const resolvedDatabaseKey = normalizeDatabaseKey(databaseKey);

  return {
    key: generateKey(),
    type: 'set_field',
    target: {
      kind: 'current_record',
      databaseKey: resolvedDatabaseKey,
      nodeTypeKey: typeKey,
    },
    fieldKey: '',
    value: {
      type: 'now',
    },
  };
};

export const buildDefaultEmailAction = () => ({
  key: generateKey(),
  type: 'send_email',
  recipientSource: {
    type: 'explicit_members',
    explicitMemberUserKeys: [],
    triggerPeopleFieldKey: '',
    relatedNodeTypeKey: '',
    relatedPeopleFieldKey: '',
  },
  message: {
    text: '',
    includedFieldKeys: [],
    omitEmptyFields: true,
  },
});

export const normalizeAction = (action, { databaseKey, typeKey }) => {
  const normalizedType = String(action?.type || '').trim().toLowerCase();
  const resolvedDatabaseKey = normalizeDatabaseKey(
    action?.target?.collectionKey || action?.target?.databaseKey,
    databaseKey,
  );

  if (normalizedType === 'send_email') {
    const rawRecipientSourceType = String(action?.recipientSource?.type || '').trim().toLowerCase();
    const recipientSourceType = [
      'explicit_members',
      'trigger_people_field',
      'related_node_people_field',
    ].includes(rawRecipientSourceType)
      ? rawRecipientSourceType
      : 'explicit_members';

    const legacyRecipientKeys = Array.isArray(action?.target?.recipientUserKeys)
      ? action.target.recipientUserKeys
      : [];
    const explicitMemberUserKeys = Array.from(new Set(
      (
        Array.isArray(action?.recipientSource?.explicitMemberUserKeys)
          ? action.recipientSource.explicitMemberUserKeys
          : legacyRecipientKeys
      )
        .map((value) => String(value || '').trim())
        .filter(Boolean),
    ));

    return {
      key: String(action?.key || generateKey()),
      type: 'send_email',
      recipientSource: {
        type: recipientSourceType,
        explicitMemberUserKeys,
        triggerPeopleFieldKey: String(action?.recipientSource?.triggerPeopleFieldKey || '').trim(),
        relatedNodeTypeKey: String(action?.recipientSource?.relatedNodeTypeKey || '').trim(),
        relatedPeopleFieldKey: String(action?.recipientSource?.relatedPeopleFieldKey || '').trim(),
      },
      message: {
        text: String(action?.message?.text || '').trim(),
        includedFieldKeys: Array.from(new Set(
          (Array.isArray(action?.message?.includedFieldKeys) ? action.message.includedFieldKeys : [])
            .map((value) => String(value || '').trim())
            .filter(Boolean),
        )),
        omitEmptyFields: true,
      },
    };
  }

  return {
    key: String(action?.key || generateKey()),
    type: 'set_field',
    target: {
      kind: 'current_record',
      collectionKey: resolvedDatabaseKey,
      databaseKey: resolvedDatabaseKey,
      nodeTypeKey: typeKey,
    },
    fieldKey: String(action?.fieldKey || '').trim(),
    value: {
      type: ['null', 'now', 'add', 'remove'].includes(action?.value?.type)
        ? action.value.type
        : 'literal',
      literal: ensureLiteralValue(action?.value),
    },
  };
};

export const buildDefaultCondition = ({ defaultFieldKey }) => ({
  key: generateKey(),
  fieldKey: defaultFieldKey,
  operator: getDefaultConditionOperator('string'),
  value: {
    type: 'literal',
    literal: buildDefaultConditionLiteral('string', getDefaultConditionOperator('string')),
  },
});

export const normalizeCondition = (condition, { defaultFieldKey }) => {
  const conditionFieldKey = String(condition?.fieldKey || defaultFieldKey || '').trim();
  const conditionFieldType = String(condition?.fieldType || '').trim().toLowerCase();
  const normalizedOperator = normalizeConditionOperator(condition?.operator, conditionFieldType);
  const safeOperator = normalizedOperator || getDefaultConditionOperator(conditionFieldType || 'string');
  const literalValue = ensureLiteralValue(condition?.value);
  const defaultLiteral = buildDefaultConditionLiteral(conditionFieldType || 'string', safeOperator);

  const normalizedLiteral = literalValue == null && isDateLikeFieldType(conditionFieldType)
    ? defaultLiteral
    : literalValue;

  return {
    key: String(condition?.key || generateKey()),
    fieldKey: conditionFieldKey,
    operator: safeOperator,
    value: {
      type: 'literal',
      literal: normalizedLiteral,
    },
  };
};

export const buildDefaultConditionGroup = ({ defaultFieldKey }) => ({
  key: generateKey(),
  conditions: [buildDefaultCondition({ defaultFieldKey })],
});

export const normalizeJoinToNext = (joinToNext) => (
  String(joinToNext || '').trim().toLowerCase() === 'or' ? 'or' : 'and'
);

export const flattenConditionGroups = (conditionGroups = []) => {
  const groups = Array.isArray(conditionGroups) ? conditionGroups : [];
  const rows = [];

  groups.forEach((group, groupIndex) => {
    const conditions = Array.isArray(group?.conditions) ? group.conditions : [];
    conditions.forEach((condition, conditionIndex) => {
      const isLastInGroup = conditionIndex === conditions.length - 1;
      const hasNextGroup = groupIndex < groups.length - 1;

      rows.push({
        ...condition,
        joinToNext: isLastInGroup && hasNextGroup ? 'or' : 'and',
      });
    });
  });

  return rows;
};

export const AUTOMATION_TRIGGER_TYPE_OPTIONS = [
  { value: 'field_changed', label: 'Field changed' },
  { value: 'record_created', label: 'Record created' },
];

export const normalizeAutomationTriggerType = (triggerType) => {
  const normalized = String(triggerType || '').trim().toLowerCase();
  return normalized === 'record_created' ? 'record_created' : 'field_changed';
};

export const buildConditionGroupsFromRows = (rows, { defaultFieldKey, allowEmpty = false } = {}) => {
  if ((!Array.isArray(rows) || !rows.length) && allowEmpty) {
    return [];
  }

  const safeRows = Array.isArray(rows) && rows.length
    ? rows
    : [buildDefaultCondition({ defaultFieldKey })];

  const groups = [];
  let currentGroupConditions = [];

  safeRows.forEach((row, index) => {
    currentGroupConditions.push(normalizeCondition(row, { defaultFieldKey }));

    const isLastRow = index === safeRows.length - 1;
    const joinToNext = normalizeJoinToNext(row?.joinToNext);

    if (joinToNext === 'or' || isLastRow) {
      groups.push({
        key: generateKey(),
        conditions: currentGroupConditions,
      });
      currentGroupConditions = [];
    }
  });

  if (groups.length) return groups;
  return allowEmpty ? [] : [buildDefaultConditionGroup({ defaultFieldKey })];
};

export const normalizeConditionGroups = (
  rule,
  {
    defaultFieldKey,
    // Instruction-authored rules (e.g. record_created) often have no conditions.
    // Do not invent a placeholder Due/Status row just because the list is empty.
    allowEmpty = false,
  } = {},
) => {
  const rawGroups = Array.isArray(rule?.conditionGroups) ? rule.conditionGroups : [];
  if (rawGroups.length) {
    const normalizedGroups = rawGroups.map((group) => {
      const rawConditions = Array.isArray(group?.conditions) ? group.conditions : [];
      return {
        key: String(group?.key || generateKey()),
        conditions: rawConditions.length
          ? rawConditions.map((condition) => normalizeCondition(condition, { defaultFieldKey }))
          : (allowEmpty ? [] : [buildDefaultCondition({ defaultFieldKey })]),
      };
    }).filter((group) => !allowEmpty || group.conditions.length > 0);

    if (normalizedGroups.length) return normalizedGroups;
    return allowEmpty ? [] : [buildDefaultConditionGroup({ defaultFieldKey })];
  }

  const rawConditions = Array.isArray(rule?.conditions) ? rule.conditions : [];
  if (!rawConditions.length) {
    return allowEmpty ? [] : [buildDefaultConditionGroup({ defaultFieldKey })];
  }

  return [{
    key: generateKey(),
    conditions: rawConditions.map((condition) => normalizeCondition(condition, { defaultFieldKey })),
  }];
};

export const buildDefaultRule = ({ databaseKey, typeKey, defaultFieldKey }) => {
  const resolvedDatabaseKey = normalizeDatabaseKey(databaseKey);

  return {
    key: generateKey(),
    enabled: true,
    title: '',
    description: '',
    scope: {
      databaseKey: resolvedDatabaseKey,
      nodeTypeKey: typeKey,
    },
    trigger: {
      type: 'field_changed',
      fieldKey: defaultFieldKey,
    },
    conditions: [buildDefaultCondition({ defaultFieldKey })],
    conditionGroups: [buildDefaultConditionGroup({ defaultFieldKey })],
    actions: [
      buildDefaultAction({ databaseKey: resolvedDatabaseKey, typeKey }),
    ],
  };
};

export const normalizeRule = (rule, { databaseKey, typeKey, defaultFieldKey }) => {
  const rawActions = Array.isArray(rule?.actions) ? rule.actions : [];
  const triggerType = String(rule?.trigger?.type || 'field_changed').trim().toLowerCase() || 'field_changed';
  const isRecordCreated = triggerType === 'record_created';
  const triggerField = isRecordCreated
    ? ''
    : String(rule?.trigger?.fieldKey || defaultFieldKey || '').trim();
  const resolvedDatabaseKey = normalizeDatabaseKey(
    rule?.scope?.databaseKey,
    databaseKey,
  );
  const normalizedActions = rawActions.length
    ? rawActions.map((action) => normalizeAction(action, {
      databaseKey: resolvedDatabaseKey,
      typeKey,
    }))
    : [buildDefaultAction({ databaseKey: resolvedDatabaseKey, typeKey })];
  const allowEmptyConditions = isRecordCreated
    || (
      (!Array.isArray(rule?.conditions) || rule.conditions.length === 0)
      && (!Array.isArray(rule?.conditionGroups) || rule.conditionGroups.length === 0)
      && rule?.instructionAuthored === true
    );

  const normalizedConditionGroups = normalizeConditionGroups(rule, {
    defaultFieldKey: triggerField || defaultFieldKey,
    allowEmpty: allowEmptyConditions,
  });
  const firstNormalizedCondition = normalizedConditionGroups[0]?.conditions?.[0] || null;
  const normalizedConditions = firstNormalizedCondition
    ? [firstNormalizedCondition]
    : [];

  return {
    key: String(rule?.key || generateKey()),
    enabled: true,
    title: getAutomationRuleTitle(rule),
    description: getAutomationRuleDescription(rule),
    scope: {
      databaseKey: resolvedDatabaseKey,
      nodeTypeKey: typeKey,
    },
    trigger: {
      type: isRecordCreated ? 'record_created' : 'field_changed',
      fieldKey: triggerField,
    },
    conditions: normalizedConditions,
    conditionGroups: normalizedConditionGroups,
    actions: normalizedActions,
    ...(String(rule?.sourceInstruction || '').trim()
      ? { sourceInstruction: String(rule.sourceInstruction).trim() }
      : {}),
    ...(rule?.instructionAuthored === true ? { instructionAuthored: true } : {}),
    ...(rule?.detachedFromInstruction === true ? { detachedFromInstruction: true } : {}),
    ...(Number.isFinite(Number(rule?.instructionCompiledAtMs))
      ? { instructionCompiledAtMs: Number(rule.instructionCompiledAtMs) }
      : {}),
  };
};

export const normalizeRulesForSave = ({
  rules,
  databaseKey,
  typeKey,
  fieldTypeByKey,
  fieldEnumOptionsByKey,
}) => {
  return rules.map((rule) => {
    const resolvedDatabaseKey = normalizeDatabaseKey(
      rule?.scope?.databaseKey,
      databaseKey,
    );
    const triggerType = String(rule?.trigger?.type || 'field_changed').trim().toLowerCase() || 'field_changed';
    const isRecordCreated = triggerType === 'record_created';
    const triggerFieldKey = isRecordCreated
      ? ''
      : String(rule?.trigger?.fieldKey || '').trim();
    const rawConditionGroups = Array.isArray(rule.conditionGroups) && rule.conditionGroups.length
      ? rule.conditionGroups
      : [{
        key: generateKey(),
        conditions: Array.isArray(rule.conditions) && rule.conditions.length
          ? rule.conditions
          : (isRecordCreated ? [] : [buildDefaultCondition({ defaultFieldKey: triggerFieldKey })]),
      }];

    const normalizedConditionGroups = rawConditionGroups
      .map((group) => {
        const normalizedConditions = (Array.isArray(group.conditions) ? group.conditions : [])
          .map((condition) => {
            const conditionFieldKey = String(condition?.fieldKey || triggerFieldKey || '').trim();
            if (!conditionFieldKey) return null;

            const conditionFieldType = fieldTypeByKey.get(conditionFieldKey) || 'string';
            const conditionEnumOptions = fieldEnumOptionsByKey.get(conditionFieldKey) || [];
            const allowedConditionOperators = getAllowedConditionOperators(conditionFieldType);
              const normalizedConditionOperator = normalizeConditionOperator(condition?.operator, conditionFieldType);
              const selectedConditionOperator = allowedConditionOperators.includes(normalizedConditionOperator)
                ? normalizedConditionOperator
              : getDefaultConditionOperator(conditionFieldType);

            return {
              key: String(condition.key || generateKey()),
              fieldKey: conditionFieldKey,
              operator: selectedConditionOperator,
              value: {
                type: 'literal',
                  literal: parseLiteral(condition?.value?.literal, conditionFieldType, conditionEnumOptions, selectedConditionOperator),
              },
            };
          })
          .filter(Boolean);

        if (!normalizedConditions.length) return null;

        return {
          key: String(group.key || generateKey()),
          conditions: normalizedConditions,
        };
      })
      .filter(Boolean);

    const safeConditionGroups = normalizedConditionGroups.length
      ? normalizedConditionGroups
      : (isRecordCreated ? [] : [buildDefaultConditionGroup({ defaultFieldKey: triggerFieldKey })]);
    const legacyConditions = safeConditionGroups[0]?.conditions || [];

    const normalizedActions = (Array.isArray(rule.actions) ? rule.actions : []).map((action) => {
      const actionType = String(action?.type || '').trim().toLowerCase();

      if (actionType === 'send_email') {
        const recipientSourceType = String(action?.recipientSource?.type || '').trim().toLowerCase();
        const normalizedRecipientSourceType = [
          'explicit_members',
          'trigger_people_field',
          'related_node_people_field',
        ].includes(recipientSourceType)
          ? recipientSourceType
          : 'explicit_members';

        const explicitMemberUserKeys = Array.from(new Set(
          (Array.isArray(action?.recipientSource?.explicitMemberUserKeys)
            ? action.recipientSource.explicitMemberUserKeys
            : []
          )
            .map((value) => String(value || '').trim())
            .filter(Boolean),
        ));
        const triggerPeopleFieldKey = String(action?.recipientSource?.triggerPeopleFieldKey || '').trim();
        const relatedNodeTypeKey = String(action?.recipientSource?.relatedNodeTypeKey || '').trim();
        const relatedPeopleFieldKey = String(action?.recipientSource?.relatedPeopleFieldKey || '').trim();
        const includedFieldKeys = Array.from(new Set(
          (Array.isArray(action?.message?.includedFieldKeys) ? action.message.includedFieldKeys : [])
            .map((value) => String(value || '').trim())
            .filter(Boolean),
        ))
          .filter((fieldKey) => isAllowedEmailBulletFieldKey(fieldKey, fieldTypeByKey));
        const text = String(action?.message?.text || '').trim();

        if (normalizedRecipientSourceType === 'explicit_members' && !explicitMemberUserKeys.length) return null;
        if (normalizedRecipientSourceType === 'trigger_people_field' && !triggerPeopleFieldKey) return null;
        if (
          normalizedRecipientSourceType === 'related_node_people_field'
          && (!relatedNodeTypeKey || !relatedPeopleFieldKey)
        ) {
          return null;
        }
        if (!text && !includedFieldKeys.length) return null;

        return {
          key: String(action.key || generateKey()),
          type: 'send_email',
          recipientSource: {
            type: normalizedRecipientSourceType,
            explicitMemberUserKeys,
            triggerPeopleFieldKey,
            relatedNodeTypeKey,
            relatedPeopleFieldKey,
          },
          message: {
            text,
            includedFieldKeys,
            omitEmptyFields: true,
          },
        };
      }

      const actionFieldType = fieldTypeByKey.get(action.fieldKey) || 'string';
      const actionEnumOptions = fieldEnumOptionsByKey.get(action.fieldKey) || [];
      const actionValueType = ['now', 'null', 'add', 'remove'].includes(action?.value?.type)
        ? action.value.type
        : 'literal';

      return {
        key: String(action.key || generateKey()),
        type: 'set_field',
        target: {
          kind: 'current_record',
          databaseKey: resolvedDatabaseKey,
          nodeTypeKey: typeKey,
        },
        fieldKey: String(action.fieldKey || '').trim(),
        value: actionValueType === 'literal' || actionValueType === 'add' || actionValueType === 'remove'
          ? {
            type: actionValueType,
            literal: parseLiteral(action?.value?.literal, actionFieldType, actionEnumOptions),
          }
          : { type: actionValueType },
      };
    }).filter(Boolean);

    const normalizedSafeActions = normalizedActions.filter((action) => (
      action?.type === 'send_email' || action?.fieldKey
    ));

    return {
      key: rule.key,
      enabled: true,
      title: getAutomationRuleTitle(rule),
      description: getAutomationRuleDescription(rule),
      scope: {
        databaseKey: resolvedDatabaseKey,
        nodeTypeKey: typeKey,
      },
      trigger: {
        type: isRecordCreated ? 'record_created' : 'field_changed',
        fieldKey: triggerFieldKey,
      },
      conditions: legacyConditions,
      conditionGroups: safeConditionGroups,
      actions: normalizedSafeActions,
      ...(String(rule?.sourceInstruction || '').trim()
        ? { sourceInstruction: String(rule.sourceInstruction).trim() }
        : {}),
      ...(rule?.instructionAuthored === true ? { instructionAuthored: true } : {}),
      ...(rule?.detachedFromInstruction === true ? { detachedFromInstruction: true } : {}),
      ...(Number.isFinite(Number(rule?.instructionCompiledAtMs))
        ? { instructionCompiledAtMs: Number(rule.instructionCompiledAtMs) }
        : {}),
    };
  });
};
